import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { setBackButtonUrl } from '../uiSlice';
import { toSentenceCase } from '../../utils/sentenceCase';
import '../avatar/avatar.css';
import './photobooth.css';

const API = process.env.REACT_APP_PHOTOBOOTH_API_URL;

// Shown in place of the photo while it's still loading/unavailable — used
// inside both the default frame.png frame and a custom admin frame image.
const PhotoPlaceholder = () => (
  <div className="pbf-photo-placeholder">
    <svg viewBox="0 0 64 64" className="pbf-placeholder-icon" aria-hidden="true">
      <path d="m14 20 3-3 2-4 26 0 2 4 3 3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="8" y="20" width="48" height="32" rx="6" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <circle cx="32" cy="36" r="10" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d="M17 10l1.6 3.4L22 15l-3.4 1.6L17 20l-1.6-3.4L12 15l3.4-1.6z" fill="currentColor" stroke="none" />
      <path d="M47 12l1.2 2.6L51 16l-2.8 1.4L47 20l-1.2-2.6L43 16l2.8-1.4z" fill="currentColor" stroke="none" />
    </svg>
    <span className="pbf-placeholder-text">Your Photo Here</span>
  </div>
);

export default function PhotoboothFinal() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate            = useNavigate();
  const dispatch            = useDispatch();

  const [imageData, setImageData] = useState(null);
  const [loading,   setLoading]   = useState(true);
  // Once the actual final photo loads, size the frame box to ITS real
  // aspect ratio instead of the frame artwork's own fixed ratio — a fixed
  // frame shape can't show 100% of an arbitrarily-shaped photo with zero
  // empty space at the same time (one always has to give: crop or
  // letterbox). Matching the box to the real photo eliminates both at
  // once; the frame border art (same box, 100%/100%) stretches slightly
  // to match, which reads far better than losing content or dead space.
  const [imgAspectRatio, setImgAspectRatio] = useState(null);

  const authHeader = () => ({
    Authorization:  `Bearer ${user?.sessionId}&${user?.organizationId}`,
    'Content-Type': 'application/json',
  });

  // The shared header's Back button reads this from Redux (see dddUI/user.jsx)
  // — it defaults to null/empty, which made Back a no-op reload on this page.
  useEffect(() => {
    dispatch(setBackButtonUrl('/avatar'));
  }, [dispatch]);

  // The <a download> attribute is ignored by browsers for a cross-origin
  // (S3) URL, and fetching it as a blob only works if the bucket sends
  // CORS headers for GETs — which it doesn't, so that fetch was throwing
  // and silently falling back to window.open(), i.e. the image just opened
  // in a new tab instead of downloading. The backend now hands back a
  // SEPARATE presigned downloadUrl with Content-Disposition: attachment
  // baked in server-side (see presigned_url()'s download_filename param in
  // app.py) — S3 sends that header itself, so a plain navigation downloads
  // it directly with no fetch/CORS involved at all.
  const handleDownload = async () => {
    const downloadUrl = imageData?.downloadUrl || imageData?.imageUrl;
    if (!downloadUrl) return;

    // Mobile enhancement: hand the file to the native share sheet so
    // "Save Image"/"Save to Photos" saves straight to the device gallery.
    // Best-effort only — if the fetch fails (no CORS on the bucket), fall
    // through to the plain downloadUrl navigation below instead of failing
    // silently the way the old window.open(imageUrl) fallback did.
    try {
      const resp = await fetch(imageData.imageUrl);
      const blob = await resp.blob();
      const file = new File([blob], 'utsav-avatar.jpg', { type: blob.type || 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Utsav Avatar' });
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return; // user cancelled the share sheet
    }

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'utsav-avatar.jpg';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => {
    if (!user?.userId) return;
    axios
      .post(
        `${API}/api/photobooth/final`,
        { userId: user.userId },
        { headers: authHeader() },
      )
      .then(res => {
        setImageData(res.data);
      })
      .catch(() =>
        Swal.fire('Error', 'Could not load your final image.', 'error').then(() =>
          navigate('/avatar'),
        ),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const isMobile = window.innerWidth <= 768;
  const bgImg    = themeData
    ? (isMobile ? themeData.background_mob : themeData.background_desk)
    : null;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${process.env.REACT_APP_S3_PATH}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  const btnStyle = {
    background: themeData?.button_background
      ? themeData.button_background
      : `linear-gradient(180deg, ${themeData?.button_color || '#c2185b'}, ${themeData?.button_color || '#8b1538'})`,
    color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
  };

  if (loading) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  // Admin's Rules-tab "Reveal page text" (reveal_page_text) specifically —
  // not "Heading" (follow_text), which is Avatar.jsx's own page heading.
  // No static fallback: if the admin hasn't set this, show nothing rather
  // than a placeholder message that isn't actually theirs.
  const dynamicMessage = toSentenceCase(themeData?.reveal_page_text);

  return (
    <div className="av-page pbf-page" style={bgStyle}>
      {/* No outer card box — content sits directly on the page background. */}
      <div className="pbf-content">
        {themeData?.festiveAvatarHeadingImage ? (
          <>
            <img
              src={process.env.REACT_APP_S3_PATH + themeData.festiveAvatarHeadingImage}
              alt="Here is your festive avatar"
              className="pbf-heading-image"
            />
            {/* The banner graphic is just a styled title — the admin's
                actual configurable message, if set, still shows underneath
                it as plain text. */}
            {dynamicMessage && <p className="pbf-subtext">{dynamicMessage}</p>}
          </>
        ) : dynamicMessage ? (
          // Ribbon banner (reusing .pba-ribbon as-is) instead of plain
          // heading text — the message itself is still fully dynamic
          // (admin's reveal_page_text).
          <div className="pba-ribbon pbf-ribbon">
            <span aria-hidden="true">✤</span> {dynamicMessage} <span aria-hidden="true">✤</span>
          </div>
        ) : null}

        <div className="pbf-frame-stage">
          {/* Plain dark box with a golden glow and rounded corners — the box's
              aspect-ratio follows the real loaded photo, so it is shown whole
              (never cropped or padded). */}
          <div
            className="pbf-photo-box"
            style={imgAspectRatio ? { aspectRatio: imgAspectRatio } : undefined}
          >
            {imageData?.imageUrl ? (
              <img
                src={imageData.imageUrl}
                alt="Your festive avatar"
                className="pbf-final-img"
                // onLoad alone never fires when the image is already cached
                // at mount (img.complete is true) — the ref callback catches
                // that case; onLoad covers the normal one.
                ref={(node) => {
                  if (node?.complete && node.naturalWidth) {
                    setImgAspectRatio(node.naturalWidth / node.naturalHeight);
                  }
                }}
                onLoad={(e) => setImgAspectRatio(e.target.naturalWidth / e.target.naturalHeight)}
              />
            ) : (
              <PhotoPlaceholder />
            )}
          </div>
        </div>

        <div className="pbf-actions">
          {imageData?.imageUrl && (
            <button
              type="button"
              className={`pbf-btn${themeData?.finalDownloadButtonImage ? ' pbf-btn-image' : ''}`}
              style={themeData?.finalDownloadButtonImage ? undefined : btnStyle}
              onClick={handleDownload}
            >
              {themeData?.finalDownloadButtonImage ? (
                <img src={process.env.REACT_APP_S3_PATH + themeData.finalDownloadButtonImage} alt="Download" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                    <path d="M12 4v11m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 18v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download
                </>
              )}
            </button>
          )}
          {themeData?.gallery_enabled !== false && (
            <button
              type="button"
              className={`pbf-btn${themeData?.finalGalleryButtonImage ? ' pbf-btn-image' : ''}`}
              style={themeData?.finalGalleryButtonImage ? undefined : btnStyle}
              onClick={() => navigate('/gallery')}
            >
              {themeData?.finalGalleryButtonImage ? (
                <img src={process.env.REACT_APP_S3_PATH + themeData.finalGalleryButtonImage} alt="View Gallery" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="8" cy="10" r="1.6" fill="currentColor" />
                    <path d="M4 17l5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  View Gallery
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
